import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, RefreshCw, Copy, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { telegramAdminService } from '@/services/telegram-admin-service';

export function TelegramWebhookStatus() {
  const [copied, setCopied] = useState(false);

  const { data: webhookInfo, isLoading, refetch } = useQuery({
    queryKey: ['telegram-webhook-info'],
    queryFn: () => telegramAdminService.getWebhookInfo(),
    retry: false,
  });

  const handleCopyUrl = () => {
    if (webhookInfo?.url) {
      navigator.clipboard.writeText(webhookInfo.url);
      setCopied(true);
      toast.success('Webhook URL copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleRefresh = () => {
    refetch();
    toast.info('Refreshing webhook info...');
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>🔗 Webhook Status</CardTitle>
          <CardDescription>
            View detailed information about your Telegram webhook configuration
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {isLoading ? (
            <div className="p-8 flex justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : webhookInfo ? (
            <>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/50">
                  <div>
                    <p className="font-medium">Webhook Status</p>
                    <p className="text-sm text-muted-foreground">
                      {webhookInfo.url ? 'Configured and Active' : 'Not Configured'}
                    </p>
                  </div>
                  {webhookInfo.url ? (
                    <Badge variant="default" className="gap-1">
                      <CheckCircle2 className="h-3 w-3" />
                      Active
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="gap-1">
                      <XCircle className="h-3 w-3" />
                      Inactive
                    </Badge>
                  )}
                </div>

                {webhookInfo.url && (
                  <>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Webhook URL</label>
                      <div className="flex gap-2">
                        <div className="flex-1 p-3 border rounded-lg bg-muted/50 font-mono text-xs break-all">
                          {webhookInfo.url}
                        </div>
                        <Button
                          size="icon"
                          variant="outline"
                          onClick={handleCopyUrl}
                        >
                          {copied ? (
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-1 p-3 border rounded-lg">
                        <p className="text-sm text-muted-foreground">Pending Updates</p>
                        <p className="text-2xl font-bold">
                          {webhookInfo.pending_update_count || 0}
                        </p>
                      </div>

                      <div className="space-y-1 p-3 border rounded-lg">
                        <p className="text-sm text-muted-foreground">Custom Certificate</p>
                        <p className="text-2xl font-bold">
                          {webhookInfo.has_custom_certificate ? 'Yes' : 'No'}
                        </p>
                      </div>
                    </div>

                    {webhookInfo.last_error_date && (
                      <div className="p-4 border border-destructive/50 rounded-lg bg-destructive/10">
                        <p className="font-medium text-destructive">Last Error</p>
                        <p className="text-sm mt-1">{webhookInfo.last_error_message}</p>
                        <p className="text-xs text-muted-foreground mt-2">
                          {new Date(webhookInfo.last_error_date * 1000).toLocaleString()}
                        </p>
                      </div>
                    )}

                    {webhookInfo.allowed_updates && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium">Allowed Updates</p>
                        <div className="flex flex-wrap gap-2">
                          {webhookInfo.allowed_updates.map((update: string) => (
                            <Badge key={update} variant="outline">
                              {update}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              <Button onClick={handleRefresh} variant="outline" className="w-full">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh Info
              </Button>
            </>
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground">
                No webhook configured. Go to Bot Configuration tab to set it up.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
